import {
  Box,
  Grid,
  GridItem,
  Heading,
  HStack,
  Button,
  IconButton,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Icon,
  Badge,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { MdArrowBack, MdRefresh } from 'react-icons/md'
import { FaHeartbeat } from 'react-icons/fa'
import { useState } from 'react'
import ImageUploadZone from './ImageUploadZone'
import AIResultsPanel from './AIResultsPanel'
import NotesPanel from './NotesPanel'
import CasesList from './CasesList'

interface SpecialtyWorkspaceProps {
  specialtyId: string
}

const specialtyNames: Record<string, string> = {
  parasitology: 'Parasitology',
  radiology: 'Radiology',
  hematology: 'Hematology',
  dermatology: 'Dermatology',
}

export default function SpecialtyWorkspace({ specialtyId }: SpecialtyWorkspaceProps) {
  const navigate = useNavigate()
  const toast = useToast()
  const [uploadedImages, setUploadedImages] = useState<string[]>([])
  const [analyzing, setAnalyzing] = useState(false)
  const [hasResults, setHasResults] = useState(false)

  const specialtyName = specialtyNames[specialtyId] || 'Unknown Specialty'

  const handleImagesUploaded = (imageUrls: string[]) => {
    setUploadedImages(imageUrls)
    // Simulate AI analysis
    setAnalyzing(true)
    setTimeout(() => {
      setAnalyzing(false)
      setHasResults(true)
      toast({
        title: 'Analysis Complete',
        description: 'AI analysis has been completed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    }, 2000)
  }

  const handleNewAnalysis = () => {
    setUploadedImages([])
    setHasResults(false)
    setAnalyzing(false)
  }

  // Determine color scheme based on specialty
  const getColorScheme = () => {
    switch (specialtyId) {
      case 'hematology':
        return { gradient: 'linear(135deg, red.600 0%, red.500 50%, pink.400 100%)', icon: FaHeartbeat }
      case 'parasitology':
        return { gradient: 'linear(135deg, purple.600 0%, purple.500 50%, pink.400 100%)', icon: FaHeartbeat }
      case 'radiology':
        return { gradient: 'linear(135deg, blue.600 0%, blue.500 50%, cyan.400 100%)', icon: FaHeartbeat }
      default:
        return { gradient: 'linear(135deg, red.600 0%, red.500 50%, pink.400 100%)', icon: FaHeartbeat }
    }
  }

  const colorScheme = getColorScheme()

  return (
    <VStack spacing={4} align="stretch" h="100%">
      {/* Header */}
      <Box
        bgGradient={colorScheme.gradient}
        borderRadius="2xl"
        p={6}
        position="relative"
        overflow="hidden"
        boxShadow="2xl"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      >
        <HStack justify="space-between" position="relative" zIndex={1}>
          <HStack spacing={4}>
            <IconButton
              aria-label="Back to Lab"
              icon={<MdArrowBack />}
              onClick={() => navigate('/lab')}
              variant="ghost"
              color="white"
              _hover={{ bg: 'whiteAlpha.300' }}
              size="lg"
            />
            <Box
              p={3}
              bg="whiteAlpha.300"
              backdropFilter="blur(10px)"
              borderRadius="xl"
              border="1px solid"
              borderColor="whiteAlpha.400"
            >
              <Icon as={colorScheme.icon} boxSize={8} color="white" />
            </Box>
            <VStack spacing={1} align="start">
              <Heading size="lg" color="white" fontWeight="bold" letterSpacing="tight">
                {specialtyName} Analysis
              </Heading>
              <HStack spacing={1.5}>
                <Badge colorScheme="green" variant="solid" fontSize="2xs" px={1.5} py={0.5} borderRadius="md">
                  AI-Powered
                </Badge>
                <Badge colorScheme="orange" variant="solid" fontSize="2xs" px={1.5} py={0.5} borderRadius="md">
                  BETA
                </Badge>
              </HStack>
            </VStack>
          </HStack>
          <Button
            leftIcon={<MdRefresh />}
            onClick={handleNewAnalysis}
            variant="solid"
            bg="whiteAlpha.300"
            color="white"
            _hover={{ bg: 'whiteAlpha.400', transform: 'rotate(180deg)' }}
            transition="all 0.3s"
            size="lg"
            borderRadius="xl"
          >
            New Analysis
          </Button>
        </HStack>
      </Box>

      {/* Main Workspace Grid */}
      <Grid
        templateColumns={{ base: '1fr', lg: '1fr 400px' }}
        gap={4}
        flex={1}
        overflow="hidden"
      >
        {/* Left Side - Main Analysis Area */}
        <GridItem>
          <Tabs
            variant="enclosed"
            colorScheme="purple"
            h="100%"
            display="flex"
            flexDirection="column"
          >
            <TabList>
              <Tab>Upload & Analyze</Tab>
              <Tab>Recent Cases</Tab>
            </TabList>

            <TabPanels flex={1} overflow="auto">
              <TabPanel h="100%">
                <VStack spacing={4} align="stretch" h="100%">
                  <ImageUploadZone
                    onImagesUploaded={handleImagesUploaded}
                    uploadedImages={uploadedImages}
                    analyzing={analyzing}
                  />
                </VStack>
              </TabPanel>
              <TabPanel>
                <CasesList specialtyId={specialtyId} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </GridItem>

        {/* Right Side - Results & Notes */}
        <GridItem>
          <Tabs
            variant="enclosed"
            colorScheme="purple"
            h="100%"
            display="flex"
            flexDirection="column"
          >
            <TabList>
              <Tab>AI Results</Tab>
              <Tab>Notes</Tab>
            </TabList>

            <TabPanels flex={1} overflow="auto">
              <TabPanel h="100%">
                <AIResultsPanel
                  hasResults={hasResults}
                  analyzing={analyzing}
                  specialtyId={specialtyId}
                />
              </TabPanel>
              <TabPanel h="100%">
                <NotesPanel />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </GridItem>
      </Grid>
    </VStack>
  )
}
